// Правилото за сервиране в Маркиране — същото като проверката на BE при PATCH
// /bills/items/served (monitors/serving.py): ред се сервира чак от „сервира
// след" статуса на сервитьорския монитор, който показва станцията му. Станциите
// на реда са тези, на които мониторите го показват: неговите (ред на няколко
// станции — всичките) И на добавките му — най-разрешаващият праг от тях;
// станции без сервитьорски монитор — от всеки статус, а без нито един
// сервитьорски монитор правилото е null. Прагът на РЕДА е с таван по станциите
// му (без добавките, monitors/serveReach.ts): ред, който никой монитор не
// докарва до прага, се сервира от последния статус, до който стига.
// Разсервирането е винаги позволено.
import type {
  KitchenStatus,
  ReachableStatusByStation,
  ServeFromStatusByStation,
} from "../../../../api/monitors";
import {
  capByReachableStatus,
  reachableStatusOf,
  rowStationIdsOf,
  serveFromStatusOfStations,
  statusStationIdsOf,
} from "../../../monitors/serveReach";
import { isKitchenStatus } from "../../../monitors/visibleFromStatus";

// Правилото на заведението (GET /monitors/serve-rule); null — без ограничение.
export interface ServeRule {
  // Прагът по станция; null — стар BE без картата: важи serveFromStatus за
  // всеки ред (досегашното едно правило за заведението).
  serveFromStatusByStation: ServeFromStatusByStation | null;
  serveFromStatus: KitchenStatus;
  reachableStatusByStation: ReachableStatusByStation;
}

// Имената на статусите — същите като в легендата (StatusLegendHint).
export const SERVE_STATUS_LABEL_KEYS: Record<KitchenStatus, string> = {
  "0": "ordering.statusNotStarted",
  "1": "ordering.statusInProgress",
  "2": "ordering.statusCooked",
  "3": "ordering.statusIssued",
};

// Станциите на ред или добавка от сметката (частите на ред на няколко станции).
interface ServeRuleStationRow {
  station_id: number;
  station_statuses?: ReadonlyArray<{
    station_id: number;
    tracked?: boolean;
  }> | null;
}

// Ред от сметката, както го чете правилото: статусът, сервирането, станциите
// му и на добавките му.
interface ServeRuleItem extends ServeRuleStationRow {
  status: string;
  served: boolean;
  additions?: ReadonlyArray<ServeRuleStationRow>;
}

// Станциите, на които мониторите показват ред или добавка (BE `row_station_ids`).
const shownStationIdsOf = (row: ServeRuleStationRow): number[] =>
  rowStationIdsOf(
    row.station_statuses?.map((part) => ({ stationId: part.station_id })),
    row.station_id,
  );

// Статусът, след който редът може да се сервира, ако СЕГА не може — иначе
// null (сервиран ред, без правило, станции без сервитьорски монитор, или вече
// стигнал прага си). Непознат статус на реда се чете като „Незапочнато".
export const serveBlockedUntilOf = (
  item: ServeRuleItem,
  serveRule: ServeRule | null,
): KitchenStatus | null => {
  if (item.served || serveRule == null) return null;
  const parts = item.station_statuses?.map((part) => ({
    stationId: part.station_id,
    tracked: part.tracked,
  }));
  // Станционният канал праща ястието и на станцията на добавката му — там
  // „Изпълни" сервира този ред (BE `item_and_additions_station_ids`).
  const threshold = serveRule.serveFromStatusByStation
    ? serveFromStatusOfStations(
        [item, ...(item.additions ?? [])].flatMap(shownStationIdsOf),
        serveRule.serveFromStatusByStation,
      )
    : serveRule.serveFromStatus;
  if (threshold == null) return null;
  const rowServeFrom = capByReachableStatus(
    threshold,
    reachableStatusOf(
      statusStationIdsOf(parts, item.station_id),
      serveRule.reachableStatusByStation,
    ),
  );
  const rowStatus = isKitchenStatus(item.status) ? item.status : "0";
  return rowStatus < rowServeFrom ? rowServeFrom : null;
};
